import {
  type ESettingKey,
  type IHolidayPeriod,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import { type ISettingRecord, type ISettingsState } from './types';

export const preloadedState: ISettingsState = {
  ...asyncStateInitial,
  byKey: {},
};

const settingsSlice = createSlice({
  name: 'Settings',
  initialState: preloadedState,
  reducers: {
    settingsLoadAll: {
      reducer(state) {
        startLoading(state);
      },
      prepare(key: ESettingKey) {
        return { payload: { key } };
      },
    },
    settingsLoadAllResponse: {
      reducer(
        state,
        action: { payload: { key: ESettingKey; records: ISettingRecord[] } },
      ) {
        state.byKey[action.payload.key] = action.payload.records;
        finishLoading(state);
      },
      prepare(key: ESettingKey, records: ISettingRecord[]) {
        return { payload: { key, records } };
      },
    },
    settingsAdd: {
      reducer(state) {
        startLoading(state);
      },
      prepare(key: ESettingKey, value: IHolidayPeriod) {
        return { payload: { key, value } };
      },
    },
    settingsAddResponse: {
      reducer(
        state,
        action: {
          payload: { key: ESettingKey; record: ISettingRecord | null };
        },
      ) {
        const { key, record } = action.payload;
        if (record) {
          state.byKey[key] = [...(state.byKey[key] ?? []), record];
        }
        finishLoading(state);
      },
      prepare(key: ESettingKey, record: ISettingRecord | null) {
        return { payload: { key, record } };
      },
    },
    settingsDelete: {
      reducer(state) {
        startLoading(state);
      },
      prepare(key: ESettingKey, id: number) {
        return { payload: { key, id } };
      },
    },
    settingsDeleteResponse: {
      reducer(state, action: { payload: { key: ESettingKey; id: number } }) {
        const { key, id } = action.payload;
        const records = state.byKey[key];
        if (records) {
          state.byKey[key] = records.filter(
            (record: ISettingRecord): boolean => record.id !== id,
          );
        }
        finishLoading(state);
      },
      prepare(key: ESettingKey, id: number) {
        return { payload: { key, id } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Settings'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  settingsAdd,
  settingsAddResponse,
  settingsDelete,
  settingsDeleteResponse,
  settingsLoadAll,
  settingsLoadAllResponse,
} = settingsSlice.actions;
export default settingsSlice.reducer;
